import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { ApiResponse } from 'src/app/models/api-response.model';
import { SnackService } from 'src/app/services/snack.service';
import { environment } from 'src/environments/environment';

export type AdminReportFileRef =
  | { type: 'client-audit' | 'claim-history'; clientId: string; docId: string }
  | { type: 'twilio' | 'bulk-load-log' | 'claim-job-log'; docId: string };

@Injectable({
  providedIn: 'root',
})
export class AdminReportFilesService {
  constructor(
    private http: HttpClient,
    private snackService: SnackService
  ) {}

  /** Asks the API for a short-lived link to an admin report or log file. */
  getUrl(ref: AdminReportFileRef): Observable<string> {
    const segments =
      ref.type === 'client-audit' || ref.type === 'claim-history'
        ? [ref.type, ref.clientId, ref.docId]
        : [ref.type, ref.docId];
    const path = segments.map((segment) => encodeURIComponent(segment)).join('/');
    return this.http
      .get<ApiResponse<{ url: string }>>(`${environment.welbyEndpoint}/api/v1/admin-report-files/${path}/url`)
      .pipe(map((res) => res.data.url));
  }

  /** Opens the file in a new tab; call it synchronously from the click, or the popup blocker eats the tab. */
  open(ref: AdminReportFileRef): void {
    const tab = window.open('', '_blank');
    if (tab) {
      tab.opener = null;
    }
    this.getUrl(ref)
      .pipe(take(1))
      .subscribe({
        next: (url) => {
          if (tab) {
            tab.location.href = url;
          } else {
            window.open(url, '_blank', 'noopener');
          }
        },
        error: (err) => {
          tab?.close();
          this.snackService.genericSnackBar('Could not open the file', ['error-snackbar'], 5000);
          console.error(`Error in AdminReportFilesService.open(${ref.type}): ${err?.message}`);
        },
      });
  }
}
